/** EMBER & PALM — a generous, fully indexable HTML menu with food-led editorial rhythm. */
import { ArrowRight, CalendarDays } from "lucide-react";
import { Link } from "wouter";
import { PageMeta } from "@/components/PageMeta";
import { HouseBeerPromo } from "@/components/HouseBeerPromo";
import { dinnerMenu, mealAddOns, SITE, trackEvent } from "@/lib/site";
import { MENU_IMAGES } from "@/lib/menuImages";

export default function MenuPage() {
  return (
    <>
      <PageMeta
        title="Dinner Menu | ANGKOR Cambodian Flavours"
        description="Fish Amok, Khmer curries, grills, family recipes and shared feasts. ANGKOR's full dinner menu in West Melbourne, Tuesday to Sunday."
        path="/menu"
        image={SITE.images.fishAmok}
      />
      <section className="page-hero menu-page-hero">
        <img
          src={SITE.images.fishAmokRice}
          srcSet={MENU_IMAGES.hero.srcSet}
          sizes={MENU_IMAGES.hero.sizes}
          fetchPriority="high"
          loading="eager"
          alt="Fish Amok in banana leaf at ANGKOR Cambodian Flavours"
          width="1800"
          height="1200"
        />
        <div className="page-hero-overlay" />
        <div className="container page-hero-copy">
          <p className="eyebrow">Dinner · Tuesday to Sunday</p>
          <h1>
            Cambodian <em>Dinner Menu</em>
          </h1>
          <p>
            Fragrant kroeung, charcoal grills, slow-cooked comfort and family
            recipes — all made for sharing.
          </p>
          <a
            className="button button-gold"
            href={SITE.booking}
            onClick={() =>
              trackEvent("book_table_click", { placement: "menu_hero" })
            }
          >
            Reserve a Table <CalendarDays size={17} />
          </a>
        </div>
      </section>
      <section className="menu-intro">
        <div className="container intro-strip">
          <p>
            <strong>New to Cambodian food?</strong> Begin with Fish Amok, Beef
            Loc Lac or My Grandmother’s Dish. For two people, try 2–3 starters,
            2 mains and rice or roti to share.
          </p>
          <span>
            Please tell our team about allergies when ordering. Dietary options
            are available.
          </span>
        </div>
      </section>

      <HouseBeerPromo />

      <section className="section parchment-section full-menu-section">
        <div className="container full-menu-layout">
          <aside className="full-menu-nav" aria-label="Dinner menu categories">
            <p className="eyebrow dark">Explore the table</p>
            {dinnerMenu.map((category, index) => (
              <a href={`#menu-${index + 1}`} key={category.name}>
                <span>0{index + 1}</span>
                {category.name}
              </a>
            ))}
            <a href="#add-ons">
              <span>06</span>Add to Your Meal
            </a>
            <a href="#banquets">
              <span>07</span>Restaurant Banquets
            </a>
          </aside>
          <div className="full-menu-content">
            {dinnerMenu.map((category, index) => (
              <section
                className="menu-category-block"
                id={`menu-${index + 1}`}
                key={category.name}
              >
                <div className="menu-category-image">
                  <img
                    src={MENU_IMAGES.categories[index].src}
                    srcSet={MENU_IMAGES.categories[index].srcSet}
                    sizes={MENU_IMAGES.sectionSizes}
                    alt={MENU_IMAGES.categories[index].alt}
                    width="1200"
                    height="900"
                    loading="lazy"
                  />
                  <span>0{index + 1}</span>
                </div>
                <div className="menu-category-heading">
                  <p>0{index + 1}</p>
                  <h2>{category.name}</h2>
                </div>
                <div className="menu-lines">
                  {category.items.map(item => (
                    <article key={item.name}>
                      <div>
                        <h3>{category.name === "Signatures" && item.name === "Fish Amok" ? <Link href="/fish-amok-melbourne">{item.name}</Link> : item.name}</h3>
                        {"tag" in item && item.tag && (
                          <span className="menu-tag">{item.tag}</span>
                        )}
                        <p>{item.description}</p>
                      </div>
                      <strong>{item.price}</strong>
                    </article>
                  ))}
                </div>
              </section>
            ))}

            <section className="menu-category-block" id="add-ons">
              <div className="menu-category-heading">
                <p>06</p>
                <h2>Add to Your Meal</h2>
              </div>
              <div className="menu-lines">
                {mealAddOns.map(([name, price]) => (
                  <article key={name}>
                    <div>
                      <h3>{name}</h3>
                    </div>
                    <strong>{price}</strong>
                  </article>
                ))}
              </div>
            </section>

            <section
              className="menu-spotlight"
              aria-label="BBQ Beef Steak spotlight"
            >
              <img
                src={SITE.images.bbqBeef}
                srcSet={MENU_IMAGES.bbqSrcSet}
                sizes={MENU_IMAGES.sectionSizes}
                alt="BBQ Beef Steak with grilled vegetables at ANGKOR Cambodian Flavours"
                width="1600"
                height="2000"
                loading="lazy"
              />
              <div>
                <p className="eyebrow">From the grill</p>
                <h2>
                  Char, spice and <em>a generous table.</em>
                </h2>
                <p>
                  Our BBQ Beef Steak brings a full 300-gram chargrilled rump cap
                  to the table with mixed vegetables and black pepper salt with
                  lemon.
                </p>
                <a href={SITE.booking} className="button button-gold">
                  Book for Dinner
                </a>
              </div>
            </section>

            <section className="menu-category-block" id="banquets">
              <div className="menu-category-image">
                <img
                  src={SITE.images.khmerGrill}
                  srcSet={MENU_IMAGES.banquetSrcSet}
                  sizes={MENU_IMAGES.sectionSizes}
                  alt="Shared Khmer banquet table at ANGKOR Cambodian Flavours"
                  width="1400"
                  height="900"
                  loading="lazy"
                />
                <span>07</span>
              </div>
              <div className="menu-category-heading">
                <p>07</p>
                <h2>Restaurant Banquet Packages</h2>
              </div>
              <div className="banquet-cards">
                <article>
                  <p>Best seller</p>
                  <h3>Angkor Classics Feast</h3>
                  <strong>
                    $79 <small>per person</small>
                  </strong>
                  <span>
                    Betel Leaf, Spring Rolls, Fish Amok, Red Curry Chicken, Roti
                    or Coconut Rice, Sticky Rice with Mango.
                  </span>
                </article>
                <article>
                  <p>A fuller table</p>
                  <h3>Royal Khmer Restaurant Feast</h3>
                  <strong>
                    $99 <small>per person</small>
                  </strong>
                  <span>
                    Fish Cakes, Prawn Wontons, Papaya Salad, My Grandmother’s
                    Dish, Beef Loc Lac, Red Curry Chicken, Jasmine Rice, Nom
                    Banh Chok.
                  </span>
                </article>
              </div>
              <p className="menu-footnote">
                Regular restaurant dining · minimum two guests. Event feasts are
                separate packages for a minimum of 12 adults.
              </p>
              <Link href="/groups-functions" className="text-link">
                Explore event packages <ArrowRight size={16} />
              </Link>
            </section>
          </div>
        </div>
      </section>
      <section className="section dark-callout">
        <div className="container narrow-cta">
          <div>
            <p className="eyebrow">Pull up a chair</p>
            <h2>
              Ready for the full <em>Angkor table?</em>
            </h2>
          </div>
          <a
            href={SITE.booking}
            className="button button-gold"
            onClick={() =>
              trackEvent("book_table_click", { placement: "menu_bottom" })
            }
          >
            Book a Table
          </a>
        </div>
      </section>
    </>
  );
}
